import { useBlocker } from '@tanstack/react-router'
import { UnsavedChangesDialog } from '@/components/custom/unsaved-changes-dialog'

interface UseUnsavedChangesBlockerOptions {
  /** Whether there are unsaved changes that should block navigation */
  hasUnsavedChanges: boolean
  /** Optional callback when user chooses to leave */
  onLeave?: () => void
}

/**
 * Hook that blocks navigation when there are unsaved changes and shows a confirmation dialog.
 * Uses TanStack Router's useBlocker under the hood.
 *
 * Only blocks navigation to different routes - search param changes on the same page
 * (like opening a side-peek) are allowed without confirmation.
 *
 * @example
 * ```tsx
 * const { UnsavedChangesBlocker } = useUnsavedChangesBlocker({
 *   hasUnsavedChanges,
 *   onLeave: () => revertAllChanges(),
 * })
 *
 * return (
 *   <>
 *     <YourContent />
 *     <UnsavedChangesBlocker />
 *   </>
 * )
 * ```
 */
export function useUnsavedChangesBlocker({
  hasUnsavedChanges,
  onLeave,
}: UseUnsavedChangesBlockerOptions) {
  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      // Don't block if no unsaved changes
      if (!hasUnsavedChanges) return false

      // Only block if navigating to a different route (pathname change)
      // Allow search param changes on the same page (e.g., opening side-peek)
      return current.pathname !== next.pathname
    },
    withResolver: true,
    enableBeforeUnload: hasUnsavedChanges,
  })

  const isBlocked = status === 'blocked'

  const handleLeave = () => {
    onLeave?.()
    proceed?.()
  }

  const handleStay = () => {
    reset?.()
  }

  /**
   * Component to render the unsaved changes dialog.
   * Place this at the end of your component's JSX.
   */
  const UnsavedChangesBlocker = () => (
    <UnsavedChangesDialog
      open={isBlocked}
      onLeave={handleLeave}
      onStay={handleStay}
    />
  )

  return {
    /** Whether navigation is currently blocked */
    isBlocked,
    /** Proceed with the blocked navigation */
    proceed: handleLeave,
    /** Cancel the blocked navigation and stay on the page */
    reset: handleStay,
    /** Component to render - place at end of your JSX */
    UnsavedChangesBlocker,
  }
}
